import Card from '../ui/Card';
import { MODULE_LABELS, type Assessment } from '../../mentorshipService';

type Props = {
    assessment: Assessment;
};

function ModuleAssessmentCard({ assessment: a }: Props) {
    if (a.state !== 'ready') {
        return (
            <Card variant="stacked" className="module-assessment">
                <Card.Header>
                    <h3>AI assessment</h3>
                </Card.Header>
                <Card.Body>
                    {a.state === 'pending_ai' && <p>AI is still analyzing this submission…</p>}
                    {a.state === 'schema_error' && (
                        <p className="module-assessment__error">
                            AI assessment failed: {a.error_detail || 'unknown error'}
                        </p>
                    )}
                </Card.Body>
            </Card>
        );
    }

    const tp = a.signals?.thinking_patterns;

    return (
        <Card variant="stacked" className="module-assessment">
            <Card.Header>
                <span className="mentorship-chip mentorship-chip--module">
                    {MODULE_LABELS[a.module]}
                </span>
                <div className="module-assessment__score">
                    <span className="module-assessment__score-num">{a.score_ai}</span>
                    <span className="module-assessment__score-suffix">/5</span>
                </div>
                {a.confidence !== null && (
                    <span className="module-assessment__confidence">
                        AI confidence {Math.round(a.confidence * 100)}%
                    </span>
                )}
            </Card.Header>
            <Card.Body>
                <details open className="module-assessment__section">
                    <summary>Justification</summary>
                    {a.justification.strengths.length > 0 && (
                        <>
                            <h4>Strengths</h4>
                            <ul>
                                {a.justification.strengths.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                        </>
                    )}
                    {a.justification.gaps.length > 0 && (
                        <>
                            <h4>Gaps</h4>
                            <ul>
                                {a.justification.gaps.map((g, i) => <li key={i}>{g}</li>)}
                            </ul>
                        </>
                    )}
                    {a.justification.incorrect_assumptions.length > 0 && (
                        <>
                            <h4>Incorrect assumptions</h4>
                            <ul>
                                {a.justification.incorrect_assumptions.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                        </>
                    )}
                </details>

                <details className="module-assessment__section">
                    <summary>Signals</summary>
                    {a.signals.keywords.length > 0 && (
                        <div className="module-assessment__chips">
                            <h4>Keywords</h4>
                            {a.signals.keywords.map((k) => (
                                <span key={k} className="mentorship-chip mentorship-chip--module">{k}</span>
                            ))}
                        </div>
                    )}
                    {a.signals.missing_concepts.length > 0 && (
                        <div className="module-assessment__chips">
                            <h4>Missing concepts</h4>
                            {a.signals.missing_concepts.map((k) => (
                                <span key={k} className="mentorship-chip mentorship-chip--sla-warn">{k}</span>
                            ))}
                        </div>
                    )}
                    {a.signals.risk_awareness.length > 0 && (
                        <div className="module-assessment__chips">
                            <h4>Risk awareness</h4>
                            {a.signals.risk_awareness.map((k) => (
                                <span key={k} className="mentorship-chip mentorship-chip--module">{k}</span>
                            ))}
                        </div>
                    )}
                    {tp && (
                        <p className="module-assessment__patterns">
                            Proactive: {tp.proactive ? 'yes' : 'no'} ·
                            If/then: {tp.if_then ? 'yes' : 'no'}
                        </p>
                    )}
                </details>

                {a.flags.length > 0 && (
                    <div className="module-assessment__chips">
                        <h4>Flags</h4>
                        {a.flags.map((f) => (
                            <span key={f} className="mentorship-chip mentorship-chip--sla-breach">{f}</span>
                        ))}
                    </div>
                )}

                {a.mentor_prompts.length > 0 && (
                    <details open className="module-assessment__section">
                        <summary>AI prompts for the mentor</summary>
                        <ul>
                            {a.mentor_prompts.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                    </details>
                )}
            </Card.Body>
        </Card>
    );
}

export default ModuleAssessmentCard;
